import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { ENVS } from "config";
import { parseMaintenanceDetails } from "utils/parseMaintenanceDetails";
import type { HealthCheckResult } from "./useHealthCheck.types";
import type { UseHealthCheckOptions } from "./useHealthCheck.types";

const HEALTH_URL = `${(ENVS.backendUrl ?? "").replace(/\/+$/, "")}/health`;
const HEALTH_QUERY_KEY = ["health"] as const;

async function fetchHealth(): Promise<HealthCheckResult> {
  const res = await axios.get(HEALTH_URL, {
    validateStatus: () => true,
    timeout: 10000,
    withCredentials: false,
  });
  const statusCode = res.status;

  if (statusCode >= 200 && statusCode < 300) {
    return { status: "healthy", ok: true, statusCode };
  }
  if (statusCode === 503) {
    return {
      status: "maintenance",
      ok: false,
      statusCode,
      maintenance: parseMaintenanceDetails(res.data),
    };
  }
  // Network / CORS failures often surface as status 0 — not maintenance.
  if (statusCode === 0) {
    return { status: "error", ok: false, statusCode: 0 };
  }
  if (statusCode >= 500 && statusCode < 600) {
    return { status: "error", ok: false, statusCode };
  }
  return { status: "unknown", ok: false, statusCode };
}

export const useHealthCheck = (options: UseHealthCheckOptions = {}) => {
  const { refetchIntervalMs, enabled = false } = options;

  return useQuery({
    queryKey: HEALTH_QUERY_KEY,
    queryFn: () => fetchHealth(),
    enabled,
    refetchInterval: refetchIntervalMs && refetchIntervalMs > 0 ? refetchIntervalMs : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnMount: enabled ? "always" : false,
    retry: false,
    staleTime: 0,
  });
};

export { HEALTH_QUERY_KEY, fetchHealth };
